import React, { useState, useMemo, useEffect } from "react";
import { Player, GolfRound, Signup, Course, TeeInfo } from "../types";
import { getHandicapInsight } from "../services/geminiService";

export interface PlayerViewProps {
  players: Player[];
  rounds: GolfRound[];
  courses: Course[];
  signups: Signup[];
  activePlayer: Player;
  onUpdateSignups: (signups: Signup[]) => void;
  onUpdatePlayer: (player: Player) => void;
}

export const PlayerView: React.FC<PlayerViewProps> = ({
  players,
  rounds,
  courses,
  signups,
  onUpdateSignups,
  onUpdatePlayer,
  activePlayer,
}) => {
  const [insight, setInsight] = useState<string | null>(null);
  const [loadingInsight, setLoadingInsight] = useState(false);
  const [playerHandicap, setPlayerHandicap] = useState<string>("");
  const [isUpdatingHandicap, setIsUpdatingHandicap] = useState(false);
  const [showCompleted, setShowCompleted] = useState(false);

  // State for the "Join Round" flow
  const [joiningRoundId, setJoiningRoundId] = useState<string | null>(null);
  const [joiningTeeId, setJoiningTeeId] = useState<string>("");

  const { upcomingRounds, completedRounds } = useMemo(() => {
    const now = new Date();
    // Set to midnight today for accurate comparison
    const today = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
    ).getTime();

    // Sort descending (newest first) as before
    const sorted = [...rounds].sort((a: GolfRound, b: GolfRound) => {
      const dateA = new Date(a.date + "T00:00:00").getTime();
      const dateB = new Date(b.date + "T00:00:00").getTime();
      return dateB - dateA;
    });

    const upcoming: GolfRound[] = [];
    const completed: GolfRound[] = [];

    sorted.forEach((round) => {
      const roundDate = new Date(round.date + "T00:00:00").getTime();
      if (roundDate < today) {
        completed.push(round);
      } else {
        upcoming.push(round);
      }
    });

    upcoming.reverse(); // Sort upcoming rounds ascending (soonest first)

    return { upcomingRounds: upcoming, completedRounds: completed };
  }, [rounds]);

  useEffect(() => {
    setPlayerHandicap(activePlayer.handicapIndex?.toString() || "");
  }, [activePlayer]);

  const handleUpdateMyHandicap = () => {
    setIsUpdatingHandicap(true);
    const updatedPlayer: Player = {
      ...activePlayer,
      handicapIndex: playerHandicap ? parseFloat(playerHandicap) : null,
    };
    onUpdatePlayer(updatedPlayer);
    setTimeout(() => {
      setIsUpdatingHandicap(false);
      setInsight(null);
    }, 500);
  };

  const calculateCourseHandicap = (
    index: number | null,
    tee: TeeInfo | undefined,
  ) => {
    if (index === null || !tee) return null;
    const result = index * (tee.slope / 113) + (tee.rating - tee.par);
    return Math.round(result);
  };

  const handleSignup = (
    roundId: string,
    status: "in" | "out" | "waitlist",
    teeId?: string,
  ) => {
    const existingIndex = signups.findIndex(
      (s: Signup) => s.roundId === roundId && s.playerId === activePlayer.id,
    );
    let newSignups = [...signups];

    if (existingIndex > -1) {
      newSignups[existingIndex] = {
        ...newSignups[existingIndex],
        status,
        teeId:
          status === "in" || status === "waitlist"
            ? (teeId ?? newSignups[existingIndex].teeId)
            : undefined,
        updatedAt: Date.now(),
      };
    } else {
      newSignups.push({
        roundId,
        playerId: activePlayer.id,
        status,
        teeId: status === "in" || status === "waitlist" ? teeId : undefined,
        updatedAt: Date.now(),
      });
    }

    onUpdateSignups(newSignups);
  };

  const getSignupInfo = (roundId: string) => {
    return signups.find(
      (s: Signup) => s.roundId === roundId && s.playerId === activePlayer.id,
    );
  };

  const getAttendeeData = (roundId: string, course: Course | undefined) => {
    return signups
      .filter((s: Signup) => s.roundId === roundId && s.status === "in")
      .map((s: Signup) => {
        const p = players.find((player: Player) => player.id === s.playerId);
        const tee = course?.tees.find((t: TeeInfo) => t.id === s.teeId);
        const courseHdcp = calculateCourseHandicap(
          p?.handicapIndex || null,
          tee,
        );
        return {
          name: p?.name,
          teeName: tee?.name,
          courseHdcp,
          handicapIndex: p?.handicapIndex,
        };
      })
      .filter((d: any) => d.name);
  };

  const renderRoundCard = (round: GolfRound) => {
    const course = courses.find((c: Course) => c.id === round.courseId);
    const signup = getSignupInfo(round.id);
    const attendees = getAttendeeData(round.id, course);

    return (
      <div
        key={round.id}
        className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden"
      >
        <div className="p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <div className="flex flex-wrap items-baseline gap-2">
              <span className="font-bold text-emerald-600 text-lg">
                {new Date(round.date + "T00:00:00").toLocaleDateString(
                  undefined,
                  { weekday: "long" },
                )}
              </span>
              <span className="font-bold text-slate-900 text-lg">
                {new Date(round.date + "T00:00:00").toLocaleDateString(
                  undefined,
                  { month: "short", day: "numeric" },
                )}{" "}
                @ {round.time}
              </span>
            </div>
            <div className="mt-1 flex flex-col gap-0.5">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-semibold text-slate-600 text-base">
                  {course?.name || "Round"}
                </span>
              </div>
              {(round.buyIn || round.assignedTeeId) && (
                <div className="flex items-center gap-2 text-xs font-bold text-slate-400">
                  {round.buyIn && <span>Buy-In: ${round.buyIn}</span>}
                  {round.buyIn && round.assignedTeeId && (
                    <span className="text-slate-300">•</span>
                  )}
                  {round.assignedTeeId && (
                    <span>
                      Tee:{" "}
                      {(() => {
                        const t = course?.tees.find(
                          (t) => t.id === round.assignedTeeId,
                        );
                        return t
                          ? `${t.name} ${t.yardage ? `(${t.yardage})` : ""}`
                          : "Assigned Tee";
                      })()}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-col gap-2 min-w-[150px]">
            {signup?.status === "in" || signup?.status === "waitlist" ? (
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-2 px-1 pb-1">
                  {signup.status === "in" ? (
                    <div className="flex items-center gap-1 text-[10px] font-black uppercase tracking-widest text-emerald-600 select-none">
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth="3"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      I'm In
                    </div>
                  ) : (
                    <div className="flex items-center gap-1 text-[10px] font-black uppercase tracking-widest text-slate-500 select-none">
                      Waitlisted
                    </div>
                  )}
                  <button
                    onClick={() => handleSignup(round.id, "out")}
                    className="px-3 py-1.5 bg-slate-100/80 text-slate-500 rounded-lg hover:text-rose-600 hover:bg-rose-50 transition-colors text-[10px] font-black uppercase tracking-widest whitespace-nowrap"
                  >
                    Drop Out
                  </button>
                </div>
                {!round.assignedTeeId && (
                  <div className="flex flex-col gap-1 mt-1">
                    <label
                      className={`text-[10px] font-black uppercase tracking-widest pl-1 ${round.groupingState !== undefined && round.groupingState !== "unassigned" ? "text-slate-300" : "text-slate-500"}`}
                    >
                      Change Tee:
                    </label>
                    <select
                      value={signup.teeId || ""}
                      onChange={(e) =>
                        handleSignup(round.id, signup.status, e.target.value)
                      }
                      disabled={
                        round.groupingState !== undefined &&
                        round.groupingState !== "unassigned"
                      }
                      className="w-full p-2 text-xs border rounded bg-emerald-50 border-emerald-200 font-bold text-emerald-800 outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <option value="">Select Tee</option>
                      {course?.tees.map((t: TeeInfo) => (
                        <option key={t.id} value={t.id}>
                          {t.name} {t.yardage ? `(${t.yardage})` : ""}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            ) : joiningRoundId === round.id ? (
              <div className="flex flex-col gap-2 animate-in fade-in zoom-in duration-200 bg-white p-2 rounded-lg border border-emerald-100 shadow-sm">
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest text-center">
                  Select Tee to Join
                </p>
                <select
                  value={joiningTeeId}
                  onChange={(e) => setJoiningTeeId(e.target.value)}
                  className="w-full p-2 text-xs border rounded bg-white border-emerald-200 font-bold text-slate-700 outline-none focus:ring-2 focus:ring-emerald-500"
                  autoFocus
                >
                  <option value="">Choose Tee...</option>
                  {course?.tees.map((t: TeeInfo) => (
                    <option key={t.id} value={t.id}>
                      {t.name} {t.yardage ? `(${t.yardage})` : ""}
                    </option>
                  ))}
                </select>
                <div className="flex gap-2 pt-1">
                  <button
                    onClick={() => {
                      if (joiningTeeId) {
                        const inSignups = signups.filter(
                          (s) => s.roundId === round.id && s.status === "in",
                        );
                        const isFull = inSignups.length >= round.maxPlayers;
                        handleSignup(
                          round.id,
                          isFull ? "waitlist" : "in",
                          joiningTeeId,
                        );
                        setJoiningRoundId(null);
                        setJoiningTeeId("");
                      }
                    }}
                    disabled={!joiningTeeId}
                    className={`flex-1 py-2 rounded-lg font-bold text-xs uppercase tracking-widest transition-all ${joiningTeeId ? "bg-emerald-600 text-white shadow-md hover:bg-emerald-700" : "bg-slate-100 text-slate-300 cursor-not-allowed"}`}
                  >
                    Confirm
                  </button>
                  <button
                    onClick={() => {
                      setJoiningRoundId(null);
                      setJoiningTeeId("");
                    }}
                    className="px-3 py-2 bg-slate-50 text-slate-400 rounded-lg font-bold text-xs hover:bg-slate-100 hover:text-slate-600 transition-colors"
                  >
                    ✕
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    const inSignups = signups.filter(
                      (s) => s.roundId === round.id && s.status === "in",
                    );
                    const isFull = inSignups.length >= round.maxPlayers;
                    if (round.assignedTeeId) {
                      handleSignup(
                        round.id,
                        isFull ? "waitlist" : "in",
                        round.assignedTeeId,
                      );
                    } else {
                      setJoiningRoundId(round.id);
                      setJoiningTeeId("");
                    }
                  }}
                  className={`flex-1 text-white font-bold py-2 rounded-lg text-sm transition-colors shadow-sm ${(() => {
                    const inSignups = signups.filter(
                      (s) => s.roundId === round.id && s.status === "in",
                    );
                    return inSignups.length >= round.maxPlayers
                      ? "bg-slate-600 hover:bg-slate-700"
                      : "bg-emerald-700 hover:bg-emerald-800";
                  })()}`}
                >
                  {(() => {
                    const inSignups = signups.filter(
                      (s) => s.roundId === round.id && s.status === "in",
                    );
                    return inSignups.length >= round.maxPlayers
                      ? "Join Waitlist"
                      : "Count Me In";
                  })()}
                </button>
                <button
                  onClick={() => handleSignup(round.id, "out")}
                  className={`px-4 py-2 rounded-lg font-bold text-sm transition-all ${signup?.status === "out" ? "bg-rose-600 text-white shadow-md" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}
                >
                  Out
                </button>
              </div>
            )}
          </div>
        </div>

        {(() => {
          const maxPlayers = round.maxPlayers || 16;
          const isFull = attendees.length >= maxPlayers;
          const isAlmostFull = !isFull && maxPlayers - attendees.length <= 2;
          const barColor = isFull
            ? "bg-rose-500"
            : isAlmostFull
              ? "bg-amber-500"
              : "bg-emerald-500";
          const textColor = isFull
            ? "text-rose-600"
            : isAlmostFull
              ? "text-amber-600"
              : "text-emerald-600";
          const percentage =
            (Math.max(0, Math.min(attendees.length, maxPlayers)) / maxPlayers) *
            100;

          return (
            <div className="px-5 pt-2 pb-1">
              <div className="relative h-4 mb-0.5 w-full">
                <div
                  className={`absolute bottom-0 text-[10px] font-black transition-all duration-300 ${textColor}`}
                  style={{
                    left: `${percentage}%`,
                    transform: `translateX(-${percentage}%)`,
                  }}
                >
                  {attendees.length}
                </div>

                {percentage < 100 && (
                  <div className="absolute right-0 bottom-0 text-[10px] font-black text-slate-300">
                    {maxPlayers}
                  </div>
                )}
              </div>
              <div className="flex gap-0.5 w-full">
                {Array.from({ length: maxPlayers }).map((_, i) => (
                  <div
                    key={i}
                    className={`flex-1 h-1.5 rounded-sm ${i < attendees.length ? barColor : "bg-slate-200"}`}
                  />
                ))}
              </div>
            </div>
          );
        })()}

        {round.groupingState === "published" && round.groups ? (
          <div className="px-5 pb-5 pt-2 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {round.groups.map((group) => {
                const groupPlayers = group.playerIds
                  .map((pid) => {
                    const p = players.find((p) => p.id === pid);
                    const s = signups.find(
                      (s) => s.playerId === pid && s.roundId === round.id,
                    );
                    const t = course?.tees.find((t) => t.id === s?.teeId);
                    const ch = calculateCourseHandicap(
                      p?.handicapIndex || null,
                      t,
                    );
                    return { player: p, signup: s, tee: t, ch };
                  })
                  .filter((x) => x.player && x.signup?.status === "in");

                return (
                  <div
                    key={group.id}
                    className="border border-slate-200 rounded-lg overflow-hidden"
                  >
                    <div className="bg-slate-100 px-3 py-2 border-b border-slate-200">
                      <h4 className="font-black text-slate-800 uppercase tracking-widest text-[10px]">
                        {group.name}
                      </h4>
                    </div>
                    <table className="w-full text-left text-[11px]">
                      <thead className="bg-slate-50 text-slate-400 font-black uppercase tracking-widest">
                        <tr>
                          <th className="px-3 py-2 border-b">Player</th>
                          <th className="px-3 py-2 border-b">Tee</th>
                          <th className="px-3 py-2 border-b text-center">CH</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-50">
                        {groupPlayers.map(({ player, tee, ch }) => (
                          <tr
                            key={player!.id}
                            className="hover:bg-emerald-50/20"
                          >
                            <td className="px-3 py-2 font-black text-slate-700">
                              {player!.name}
                              <span className="text-slate-400 font-bold ml-1 text-[9px]">
                                ({player!.handicapIndex ?? "NH"})
                              </span>
                            </td>
                            <td className="px-3 py-2">
                              {tee ? (
                                <span className="bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full text-[9px] font-black uppercase border border-emerald-100">
                                  {tee.name}
                                </span>
                              ) : (
                                <span className="text-slate-300 italic">?</span>
                              )}
                            </td>
                            <td className="px-3 py-2 text-center font-black text-emerald-700">
                              {ch ?? "-"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                );
              })}
            </div>

            {(() => {
              const groupedPlayerIds = new Set(
                round.groups?.flatMap((g) => g.playerIds) || [],
              );
              const unassignedAttendees = attendees.filter((a: any) => {
                const p = players.find((p) => p.name === a.name);
                return p && !groupedPlayerIds.has(p.id);
              });

              if (unassignedAttendees.length === 0) return null;

              return (
                <div className="mt-6 border border-amber-200 rounded-lg overflow-hidden">
                  <div className="bg-amber-50 px-3 py-2 border-b border-amber-200">
                    <h4 className="font-black text-amber-800 uppercase tracking-widest text-[10px]">
                      Pending Group Assignment
                    </h4>
                    <p className="text-[9px] text-amber-700/70 font-bold mt-0.5">
                      These players are in the round and will be assigned to a
                      group soon.
                    </p>
                  </div>
                  <table className="w-full text-left text-[11px]">
                    <thead className="bg-white text-slate-400 font-black uppercase tracking-widest">
                      <tr>
                        <th className="px-3 py-2 border-b">Player</th>
                        <th className="px-3 py-2 border-b">Tee</th>
                        <th className="px-3 py-2 border-b text-center">CH</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50 bg-white">
                      {unassignedAttendees.map((a: any, idx: number) => (
                        <tr key={idx} className="hover:bg-amber-50/20">
                          <td className="px-3 py-2 font-black text-slate-700">
                            {a.name}
                            <span className="text-slate-400 font-bold ml-1 text-[9px]">
                              ({a.handicapIndex ?? "NH"})
                            </span>
                          </td>
                          <td className="px-3 py-2">
                            {a.teeName ? (
                              <span className="bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full text-[9px] font-black uppercase border border-emerald-100">
                                {a.teeName}
                              </span>
                            ) : (
                              <span className="text-slate-300 italic">?</span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-center font-black text-emerald-700">
                            {a.courseHdcp ?? "-"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
            })()}
          </div>
        ) : (
          attendees.length > 0 && (
            <div className="px-5 pb-5 pt-2">
              <div className="overflow-hidden rounded-xl border border-slate-100">
                <table className="w-full text-left text-[11px]">
                  <thead className="bg-slate-50 text-slate-400 font-black uppercase tracking-widest">
                    <tr>
                      <th className="px-4 py-3 border-b">Player</th>
                      <th className="px-4 py-3 border-b">Tee</th>
                      <th className="px-4 py-3 border-b text-center">HI</th>
                      <th className="px-4 py-3 border-b text-center">CH</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {attendees.map((a: any, idx: number) => (
                      <tr key={idx} className="hover:bg-emerald-50/20">
                        <td className="px-4 py-3">
                          <div className="font-black text-slate-700">
                            {a.name}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          {a.teeName ? (
                            <span className="bg-emerald-50 text-emerald-700 px-2 py-1 rounded-full text-[9px] font-black uppercase border border-emerald-100">
                              {a.teeName}
                            </span>
                          ) : (
                            <span className="text-slate-300 italic">?</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center font-black text-slate-900 text-sm">
                          {a.handicapIndex ?? "-"}
                        </td>
                        <td className="px-4 py-3 text-center font-black text-emerald-700 text-sm">
                          {a.courseHdcp ?? "-"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )
        )}

        {(() => {
          const waitlistSignups = signups
            .filter((s) => s.roundId === round.id && s.status === "waitlist")
            .sort((a, b) => a.updatedAt - b.updatedAt);

          if (waitlistSignups.length === 0) return null;

          return (
            <div className="px-5 pb-5 pt-2">
              <div className="mt-2 border border-slate-200 rounded-lg overflow-hidden">
                <div className="bg-slate-50 px-3 py-2 border-b border-slate-200">
                  <h4 className="font-black text-slate-600 uppercase tracking-widest text-[10px]">
                    Waitlist
                  </h4>
                </div>
                <table className="w-full text-left text-[11px]">
                  <thead className="bg-white text-slate-400 font-black uppercase tracking-widest">
                    <tr>
                      <th className="px-3 py-2 border-b">Player</th>
                      <th className="px-3 py-2 border-b">Tee</th>
                      <th className="px-3 py-2 border-b text-center">HI</th>
                      <th className="px-3 py-2 border-b text-center">CH</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50 bg-white">
                    {waitlistSignups.map((s, idx) => {
                      const p = players.find((p) => p.id === s.playerId);
                      const t = course?.tees.find((t) => t.id === s.teeId);
                      const ch = calculateCourseHandicap(
                        p?.handicapIndex || null,
                        t,
                      );
                      return (
                        <tr key={idx} className="hover:bg-slate-50/50">
                          <td className="px-3 py-2 font-black text-slate-700">
                            {p?.name || "Unknown"}
                          </td>
                          <td className="px-3 py-2">
                            {t ? (
                              <span className="bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full text-[9px] font-black uppercase border border-emerald-100">
                                {t.name}
                              </span>
                            ) : (
                              <span className="text-slate-300 italic">?</span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-center font-black text-slate-900">
                            {p?.handicapIndex ?? "-"}
                          </td>
                          <td className="px-3 py-2 text-center font-black text-emerald-700">
                            {ch ?? "-"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })()}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
        <div className="flex items-center gap-4 mb-4">
          <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700 font-black text-xl">
            {activePlayer.name
              .split(" ")
              .map((n: string) => n[0])
              .join("")
              .substring(0, 2)
              .toUpperCase()}
          </div>
          <div>
            <h2 className="text-lg font-bold text-slate-800">
              {activePlayer.name}
            </h2>
          </div>
        </div>

        <div className="space-y-4 pt-4 border-t border-slate-100">
          <div className="flex flex-col sm:flex-row sm:items-end gap-3">
            <div className="flex-1">
              <label className="block mb-1">
                <span className="text-sm font-black text-slate-700 uppercase tracking-wider">
                  Update Handicap Index
                </span>
                <span className="text-[10px] font-bold text-slate-400 ml-2">
                  (Please Keep Your Handicap Index Up To Date)
                </span>
              </label>
              <div className="flex gap-2">
                <input
                  type="number"
                  step="0.1"
                  placeholder="None"
                  value={playerHandicap}
                  onChange={(e) => setPlayerHandicap(e.target.value)}
                  className="flex-1 p-2 bg-slate-50 border border-slate-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-emerald-500"
                />
                <button
                  onClick={handleUpdateMyHandicap}
                  disabled={isUpdatingHandicap}
                  className={`px-4 py-2 rounded-lg text-xs font-bold transition-all ${isUpdatingHandicap ? "bg-emerald-100 text-emerald-700" : "bg-emerald-600 text-white hover:bg-emerald-700"}`}
                >
                  {isUpdatingHandicap ? "✓ Saved" : "Update"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <h2 className="text-lg font-bold text-slate-800 px-1">
          Upcoming Rounds
        </h2>
        {upcomingRounds.length === 0 ? (
          <p className="px-1 text-slate-400 italic text-sm">
            No upcoming rounds scheduled.
          </p>
        ) : (
          upcomingRounds.map(renderRoundCard)
        )}

        {completedRounds.length > 0 && (
          <div className="pt-4">
            <button
              onClick={() => setShowCompleted(!showCompleted)}
              className="w-full flex justify-between items-center bg-white p-4 rounded-xl shadow-sm border border-slate-200 text-slate-600 font-bold uppercase text-xs tracking-widest hover:text-emerald-700 hover:border-emerald-200 transition-all"
            >
              <span>Completed Rounds ({completedRounds.length})</span>
              <span
                className={`transform transition-transform duration-200 ${showCompleted ? "rotate-180" : ""}`}
              >
                ▼
              </span>
            </button>
            {showCompleted && (
              <div className="space-y-4 mt-4 animate-in fade-in slide-in-from-top-2 duration-300">
                {completedRounds.map(renderRoundCard)}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
